import {useEffect, useMemo, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {MaileonTransactionType} from 'crqlar-react-common/dist/models/marketing/maileonTransactionType.model';
import {
    MailingConfigControllerService
} from 'crqlar-react-common/dist/services/correspondence/mailingConfigController.service';
import {useNewsletterAccountQuery} from 'crqlar-react-common/dist/utils/hooks/newsletter/useNewsletterAccount';
import {useTranslation} from 'react-i18next';
import {useQuery} from 'react-query';

import QuickAddTransactionTypeDialog from '../../../../../system/sectors/QuickAddTransactionTypeDialog';
import {applyEmailMode, pruneEmailMode, readEmailMode} from '../../../config/emailModes';
import {
    OTHER_RECIPIENT,
    applyRecipientOption,
    buildRecipientOptions,
    formatRecipient,
    lowerFirstLetter,
    matchRecipientOption
} from '../../../config/recipients';
import {buildAvailableVariables} from '../../../config/nodeVariables';
import {guaranteedUpstreamNodes} from '../../../config/reachability';
import NodeEditorLayout, {NodeEditorState} from '../NodeEditorLayout';
import {SingleSelect} from '../../../../../common/newSelect';
import {NodeEditorV2Props} from '../types';
import AutomationTextField from '../variables/AutomationTextField';
import type {EmailPlaceholder} from '../../../config/emailPlaceholders';
import EmailContentEditor from './EmailContentEditor';
import {EMAIL_CONTENT_HEIGHT, EMAIL_CONTENT_HEIGHT_EXPANDED} from './EmailContentPreview';
import EmailBuilderField, {EmailBuilderHandle} from './EmailBuilderField';

/**
 * The Send Email step; the mode (builder / Maileon / custom HTML) is picked in the identity panel. The recipient
 * select maps to the API's `profileId`/`email` (`config/recipients.ts`); a config no option matches shows as
 * "Someone else" and is never rewritten.
 */
const SendEmailEditorV2 = (
    {
        nodeId, nodeConfig, setNodeConfig, upstreamNodes = [], flowNodes, flowConnections, registerBeforeSave, expanded, readOnly
    }: NodeEditorV2Props
) => {
    const {t} = useTranslation();
    const contentHeight = expanded ? EMAIL_CONTENT_HEIGHT_EXPANDED : EMAIL_CONTENT_HEIGHT;

    const builder = useRef<EmailBuilderHandle>(null);
    const [addTemplateOpen, setAddTemplateOpen] = useState(false);
    // The profile-id field under "Someone else" starts open only when the node stores one.
    const [profileIdsOpen, setProfileIdsOpen] = useState(() => !!nodeConfig.profileId);

    const mode = readEmailMode(nodeConfig);
    const placeholders: EmailPlaceholder[] = Array.isArray(nodeConfig.placeholders) ? nodeConfig.placeholders : [];

    const availableVariables = useMemo(() => buildAvailableVariables(upstreamNodes), [upstreamNodes]);

    // Recipients list only steps on every path (dominators); variable pickers list all ancestors on purpose.
    const recipientSources = useMemo(
        () => guaranteedUpstreamNodes(upstreamNodes, flowNodes, flowConnections, nodeId),
        [upstreamNodes, flowNodes, flowConnections, nodeId]
    );

    const recipientOptions = useMemo(
        () => buildRecipientOptions(recipientSources, t),
        [recipientSources, t]
    );

    const selectedRecipient = matchRecipientOption(nodeConfig, recipientOptions);
    const matchedOption = recipientOptions.find(option => option.value === selectedRecipient);

    const newsletterAccount = useNewsletterAccountQuery().data;

    const transactionTypesQ = useQuery(['transactionTypes'], async () => {
        const response = await MailingConfigControllerService.getAll();
        return response || [];
    });

    const transactionTypes: MaileonTransactionType[] = transactionTypesQ.data || [];
    // Compared as text: a stored id can come back as a string while the catalogue holds numbers.
    const selectedTemplate = nodeConfig.maileonTemplateId != null
        ? transactionTypes.find(type => String(type.id) === String(nodeConfig.maileonTemplateId)) || null
        : null;

    // Seed the nearest recipient once, and only when the node never stored one.
    const seededRef = useRef(false);
    useEffect(() => {
        if (seededRef.current || readOnly) return;
        if (nodeConfig.profileId || nodeConfig.email) {
            seededRef.current = true;
            return;
        }
        if (!recipientOptions.length) return;

        seededRef.current = true;
        setNodeConfig(applyRecipientOption(nodeConfig, recipientOptions[0]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipientOptions, readOnly]);

    const write = (patch: Record<string, any>) => setNodeConfig(previous => ({...previous, ...patch}));

    // Switching modes keeps every field while the dialog is open; Save drops what the chosen mode cannot send.
    useEffect(() => {
        registerBeforeSave?.(async () => {
            try {
                const saved = await builder.current?.flush();
                return {config: pruneEmailMode({...nodeConfig, ...saved})};
            } catch { return false; }
        });
        return () => registerBeforeSave?.(null);
    }, [registerBeforeSave, nodeConfig]);

    const handleRecipient = (value: string) => {
        const option = recipientOptions.find(entry => entry.value === value) ?? null;
        // "Someone else" clears the fields, or the old variable re-matches its option and the select snaps back.
        setNodeConfig(applyRecipientOption(nodeConfig, option));
        setProfileIdsOpen(!option && !!nodeConfig.profileId);
    };

    const subject: string = nodeConfig.subject || '';
    const content: string = nodeConfig.content || '';

    const recipientSentence = matchedOption
        ? lowerFirstLetter(matchedOption.label)
        : formatRecipient(nodeConfig, t);

    const {state, summary}: {state: NodeEditorState; summary: string} = (() => {
        if (!recipientSentence) {
            return {
                state: 'wontRun',
                summary: recipientOptions.length
                    ? t('No recipient yet, pick who this email goes to.')
                    : t('Nothing before this step carries a guest. Enter an address, or put an Audience or Team members step before it.')
            };
        }

        if (mode === 'MAILEON') {
            if (!nodeConfig.maileonTemplateId) {
                return {state: 'wontRun', summary: t('Pick a Maileon template, this step cannot send without one.')};
            }

            return {
                state: 'configured',
                summary: t('Sends the Maileon template ‘{{template}}’ to {{recipient}}.', {
                    template: selectedTemplate?.name || nodeConfig.maileonTemplateId,
                    recipient: recipientSentence
                })
            };
        }

        if (!subject.trim() || !content.trim()) {
            return {state: 'wontRun', summary: t('Add a subject and content, this step cannot send without them.')};
        }

        return {
            state: 'configured',
            summary: mode === 'BUILDER'
                ? t('Sends ‘{{subject}}’ to {{recipient}}, designed in the builder.', {subject, recipient: recipientSentence})
                : t('Sends ‘{{subject}}’ to {{recipient}}, as custom HTML.', {subject, recipient: recipientSentence})
        };
    })();

    return (
        <NodeEditorLayout state={state} summary={summary} fill={expanded && mode === 'BUILDER'}>
            <Stack gap={1}>
                <SingleSelect
                    label={t('Recipient')}
                    value={selectedRecipient}
                    disabled={readOnly}
                    disableClearable
                    onChange={handleRecipient}
                    options={[
                        ...recipientOptions.map(option => ({value: option.value, label: option.label})),
                        {value: OTHER_RECIPIENT, label: t('Someone else')}
                    ]}
                />

                {selectedRecipient === OTHER_RECIPIENT && (
                    <Stack gap={0.5}>
                        <AutomationTextField
                            insertion={'step-value'}
                            disabled={readOnly}
                            label={t('Email addresses')}
                            value={nodeConfig.email || ''}
                            onChange={value => write({email: value})}
                            placeholder={'guest@example.com, team@example.com'}
                            helperText={t('Separate addresses with commas. Each address receives a separate email. Type / to choose a value from an earlier step.')}
                            variables={availableVariables}
                        />

                        <Box>
                            <Button
                                size={'small'}
                                color={'inherit'}
                                onClick={() => setProfileIdsOpen(open => !open)}
                                endIcon={profileIdsOpen ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                                sx={{color: 'text.secondary'}}
                            >
                                {t('Profile ids')}
                            </Button>
                        </Box>

                        <Collapse in={profileIdsOpen} unmountOnExit>
                            <AutomationTextField
                                insertion={'step-value'}
                                disabled={readOnly}
                                label={t('Profile ids')}
                                hideLabel
                                value={nodeConfig.profileId || ''}
                                onChange={value => write({profileId: value})}
                                helperText={t('Comma-separated profile ids, or a value from an earlier step. The guest’s stored address is used.')}
                                variables={availableVariables}
                            />
                        </Collapse>
                    </Stack>
                )}
            </Stack>

            {mode !== 'MAILEON' && (
                <AutomationTextField
                    // Unlayer deletes `[[key]]` text, so a BUILDER mail has no mappings and a plain-text subject.
                    {...(mode === 'BUILDER'
                        ? {insertion: 'none' as const}
                        : {insertion: 'placeholder' as const, placeholders})}
                    disabled={readOnly}
                    label={t('Subject')}
                    value={subject}
                    onChange={value => write({subject: value})}
                    variables={availableVariables}
                />
            )}

            {mode === 'BUILDER' && (
                <EmailBuilderField ref={builder} content={content} unlayerSourceData={nodeConfig.unlayerSourceData}
                                   onChange={write} variables={availableVariables} expanded={expanded} readOnly={readOnly}
                                   onWriteHtml={() => setNodeConfig(applyEmailMode(nodeConfig, 'CUSTOM'))}/>
            )}

            {mode === 'CUSTOM' && (
                <EmailContentEditor content={content} subject={subject} onChange={value => write({content: value})}
                                    sources={availableVariables} mappings={placeholders} readOnly={readOnly}
                                    height={contentHeight}/>
            )}

            {mode === 'MAILEON' && (
                <Stack gap={1.5}>
                    {/* The stored id keeps its type; the select compares it as text. */}
                    <SingleSelect
                        label={t('Maileon Template')}
                        placeholder={t('Select a template')}
                        value={nodeConfig.maileonTemplateId != null ? String(nodeConfig.maileonTemplateId) : ''}
                        options={transactionTypes
                            .filter(type => type.id != null)
                            .map(type => ({value: String(type.id), label: type.name || String(type.id)}))}
                        onChange={id => write({
                            maileonTemplateId: transactionTypes.find(type => String(type.id) === id)?.id
                        })}
                        loading={transactionTypesQ.isLoading || undefined}
                        disabled={readOnly}
                    />

                    <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
                        <Button
                            size={'small'}
                            variant={'outlined'}
                            disabled={readOnly}
                            onClick={() => setAddTemplateOpen(true)}
                        >
                            {t('Create new template')}
                        </Button>

                        {!!newsletterAccount?.newsletterAccountLoginUrl && (
                            <Button
                                size={'small'}
                                variant={'outlined'}
                                onClick={() => window.open(
                                    `${newsletterAccount.newsletterAccountLoginUrl}/eagle_kp_webapp/mln/campaigns/triggers`,
                                    '_blank'
                                )}
                            >
                                {t('Manage templates')}
                            </Button>
                        )}
                    </Stack>
                </Stack>
            )}

            <QuickAddTransactionTypeDialog
                open={addTemplateOpen}
                onClose={() => setAddTemplateOpen(false)}
                onAdded={transactionType => {
                    setAddTemplateOpen(false);
                    void transactionTypesQ.refetch();
                    if (transactionType.id) write({maileonTemplateId: transactionType.id});
                }}
            />

        </NodeEditorLayout>
    );
};

export default SendEmailEditorV2;
